import { useEffect, useState } from "react"
import { Form, TimePicker, Flex } from "antd"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Card } from "ui/Card"
import { Text } from "ui/Typography"
import { useWizardContext } from "../../../WizardContext"
import styles from "./MultiDaySchedule.module.css"

const ALL_DAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"]
const DAY_LABELS = {
  sunday: "Sun",
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
}

function DayToggle({ day, selected, disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={`${styles.dayToggle} ${selected ? styles.dayToggleActive : ""} ${disabled ? styles.dayToggleDisabled : ""}`}
      onClick={() => onClick(day)}
    >
      {DAY_LABELS[day]}
    </button>
  )
}

DayToggle.propTypes = {
  day: PropTypes.string.isRequired,
  selected: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
}

function DayGroupPreview({ startDay, numberOfDays, startTime }) {
  const startIndex = ALL_DAYS.indexOf(startDay)
  const groupDays = Array.from({ length: numberOfDays }, (_, i) => ALL_DAYS[(startIndex + i) % 7])
  const displayTime = startTime ? dayjs(startTime, "HH:mm").format("h:mm A") : null

  return (
    <div className={styles.previewRow}>
      <div className={styles.previewDays}>
        {groupDays.map((day, index) => (
          <span key={day} className={styles.previewDayItem}>
            {index > 0 && <span className={styles.previewArrow}>→</span>}
            <span className={index === 0 ? styles.previewStartDay : styles.previewDay}>
              {DAY_LABELS[day]}
            </span>
          </span>
        ))}
        <span className={styles.previewCount}>({numberOfDays} {numberOfDays === 1 ? "day" : "days"})</span>
      </div>
      {displayTime && <span className={styles.previewTime}>{displayTime}</span>}
    </div>
  )
}

DayGroupPreview.propTypes = {
  startDay: PropTypes.string.isRequired,
  numberOfDays: PropTypes.number.isRequired,
  startTime: PropTypes.string,
}

export const MultiDaySchedule = ({ form }) => {
  const { t } = useTranslation()
  const { isFormInitialized } = useWizardContext()

  // Local state is the source of truth; the form is kept in sync so the save
  // handler can read these for the payload. multi_day_start_days /
  // multi_day_start_time have no Form.Item, and Form.useWatch does NOT reliably
  // track unregistered fields — using local state is what makes the UI update.
  const [selectedDays, setSelectedDays] = useState(() => form.getFieldValue("multi_day_start_days") || [])
  const [startTime, setStartTime] = useState(() => form.getFieldValue("multi_day_start_time") || null)
  const numberOfDays = Form.useWatch("number_of_days", form) || 2

  // Each selected start day occupies `span` consecutive weekdays. A start event
  // can't begin on a day whose own span would overlap an already-occupied day —
  // that would double-book a day across two multi-day events.
  const span = Math.min(numberOfDays, 7)

  // Drop the selected start days whose spans mutually overlap, keeping the
  // earlier selection. Used both to prune existing state and to compute which
  // days are occupied for the disable check.
  const pruneOverlaps = (days) => {
    const occupied = new Set()
    const kept = []
    days.forEach((day) => {
      const start = ALL_DAYS.indexOf(day)
      let conflict = false
      for (let offset = 0; offset < span; offset += 1) {
        if (occupied.has((start + offset) % 7)) { conflict = true; break }
      }
      if (!conflict) {
        for (let offset = 0; offset < span; offset += 1) occupied.add((start + offset) % 7)
        kept.push(day)
      }
    })
    return { kept, occupied }
  }

  const { occupied: occupiedIndices } = pruneOverlaps(selectedDays)

  // A day is disabled when it isn't already a start day and starting an event
  // there would overlap an occupied day (forward or backward, with wraparound).
  const isDayDisabled = (day) => {
    if (selectedDays.includes(day)) return false
    const start = ALL_DAYS.indexOf(day)
    for (let offset = 0; offset < span; offset += 1) {
      if (occupiedIndices.has((start + offset) % 7)) return true
    }
    return false
  }

  // Re-read from the form once WizardProvider has populated it (the user may land
  // on this tab before the trip finishes loading on edit).
  useEffect(() => {
    if (!isFormInitialized) return
    setSelectedDays(form.getFieldValue("multi_day_start_days") || [])
    setStartTime(form.getFieldValue("multi_day_start_time") || null)
  }, [isFormInitialized, form])

  // Changing the day-count can turn previously valid start days into overlapping
  // ones — prune the conflicting later selections so the payload stays valid.
  useEffect(() => {
    setSelectedDays((prev) => {
      const { kept } = pruneOverlaps(prev)
      if (kept.length === prev.length) return prev
      form.setFieldValue("multi_day_start_days", kept)
      return kept
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberOfDays])

  const toggleDay = (day) => {
    setSelectedDays((prev) => {
      const next = prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
      form.setFieldValue("multi_day_start_days", next)
      return next
    })
  }

  const handleTimeChange = (time) => {
    const formatted = time ? time.format("HH:mm") : null
    setStartTime(formatted)
    form.setFieldValue("multi_day_start_time", formatted)
  }

  return (
    <Card
      className="mb-3xl"
      title={t("listings.tabs.scheduling.sections.multi_day_schedule.title", { defaultValue: "Multi-Day Availability" })}
    >
      {/* Start Days */}
      <div className="mb-l">
        <Text fw={500} className="block mb-xs">
          {t("listings.tabs.scheduling.sections.multi_day_schedule.start_days", { defaultValue: "Start Days" })}
          <span style={{ color: "var(--color-error)", marginLeft: 2 }}>*</span>
        </Text>
        <Text type="secondary" size="sm" className="block mb-m">
          {t("listings.tabs.scheduling.sections.multi_day_schedule.start_days_hint", {
            defaultValue:
              "Select which days the experience can begin. Each chosen day groups {{count}} consecutive days.",
            count: numberOfDays,
          })}
        </Text>
        <Flex gap={8} wrap="wrap">
          {ALL_DAYS.map((day) => (
            <DayToggle
              key={day}
              day={day}
              selected={selectedDays.includes(day)}
              disabled={isDayDisabled(day)}
              onClick={toggleDay}
            />
          ))}
        </Flex>
      </div>

      {/* Start Time */}
      <div className="mb-l">
        <Text fw={500} className="block mb-s">
          {t("listings.tabs.scheduling.sections.multi_day_schedule.start_time", { defaultValue: "Start Time" })}
          <span style={{ color: "var(--color-error)", marginLeft: 2 }}>*</span>
        </Text>
        <TimePicker
          needConfirm={false}
          use12Hours
          format="h:mm A"
          minuteStep={15}
          value={startTime ? dayjs(startTime, "HH:mm") : null}
          onChange={handleTimeChange}
          style={{ width: 180 }}
        />
      </div>

      {/* Schedule Preview */}
      {selectedDays.length > 0 && (
        <div className={styles.previewSection}>
          <Text fw={600} size="sm" className="block mb-s">
            {t("listings.tabs.scheduling.sections.multi_day_schedule.schedule_preview", {
              defaultValue: "Schedule Preview",
            })}
          </Text>
          <div className={styles.previewList}>
            {selectedDays.map((day) => (
              <DayGroupPreview
                key={day}
                startDay={day}
                numberOfDays={numberOfDays}
                startTime={startTime}
              />
            ))}
          </div>
        </div>
      )}
    </Card>
  )
}

MultiDaySchedule.propTypes = {
  form: PropTypes.object.isRequired,
}
